import React, { useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import { formatDuration } from './chatHelpers';

/** Inline audio player for voice/audio attachments. Play/pause + seekable
 * progress bar + running duration. Generic — relocated from schoolmate_dev's
 * ChatComps (same component, restyled to this app's tokens). */
export default function AudioMessage({ src, mine, accent = '#7C5CFC' }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (playing) a.pause(); else a.play();
    };

    const onSeek = (e) => {
        const a = audioRef.current;
        if (!a || !dur) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        a.currentTime = ratio * dur;
        setCur(a.currentTime);
    };

    const pct = dur ? (cur / dur) * 100 : 0;
    const barColor = mine ? 'rgba(255,255,255,0.9)' : accent;
    const trackColor = mine ? 'rgba(255,255,255,0.28)' : '#E2E4EE';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 220, maxWidth: '100%' }}>
            <IconButton
                onClick={toggle}
                size="small"
                sx={{
                    width: 30, height: 30, flexShrink: 0,
                    bgcolor: mine ? 'rgba(255,255,255,0.18)' : '#F1EEFE',
                    '&:hover': { bgcolor: mine ? 'rgba(255,255,255,0.3)' : '#E7DFFC' },
                }}
            >
                {playing ? <PauseRoundedIcon sx={{ fontSize: 18, color: mine ? '#fff' : accent }} />
                    : <PlayArrowRoundedIcon sx={{ fontSize: 18, color: mine ? '#fff' : accent }} />}
            </IconButton>
            <Box onClick={onSeek} sx={{ flexGrow: 1, minWidth: 0, height: 5, borderRadius: 3, bgcolor: trackColor, cursor: 'pointer', position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, bgcolor: barColor, borderRadius: 3 }} />
                <Box sx={{ position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%, -50%)', width: 11, height: 11, borderRadius: '50%', bgcolor: barColor, boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
            </Box>
            <Typography sx={{ fontSize: 11, flexShrink: 0, minWidth: 30, textAlign: 'right', color: mine ? 'rgba(255,255,255,0.75)' : '#94A3B8' }}>
                {playing || cur ? formatDuration(cur) : formatDuration(dur)}
            </Typography>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                style={{ display: 'none' }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setCur(0); }}
                onTimeUpdate={(e) => setCur(e.target.currentTime)}
                onLoadedMetadata={(e) => setDur(e.target.duration)}
            >
                <track kind="captions" />
            </audio>
        </Box>
    );
}
